// Story 4.4 — TanStack mutation hook for the share-flag POST endpoints.
//
// One signature drives BOTH endpoints:
//   POST /api/v1/vault/shares/:id/highlight  { state: boolean }
//   POST /api/v1/vault/shares/:id/favourite  { state: boolean }
//
// The `action` parameter ("highlight" | "favourite") composes into the
// URL + the cache-key derivation. Set-style body (idempotent under
// retry: same body = same result) so no idempotency-key header is
// needed.
//
// Optimistic-update pattern (TanStack Query canonical recipe):
//   * onMutate: cancel in-flight ["share-detail", shareId] refetches +
//     snapshot the previous cache + write the optimistic value so the
//     <Switch> reflects the new state instantly.
//   * onError: roll back to the pre-mutation snapshot.
//   * onSettled: invalidate ["share-detail", shareId] + ["vault-items"]
//     to reconcile against server truth.
//
// Consumers: ShareDetailPage.tsx Highlight + Favourite <Switch> toggles.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  ShareDetailResponse,
  ShareToggleAction,
  ShareToggleResponse,
} from "./vaultTypes";

export interface ShareToggleError {
  code: string;
  message: string;
  status: number;
}

export interface ShareToggleVariables {
  state: boolean;
}

interface UseShareToggleArgs {
  shareId: string;
  action: ShareToggleAction;
}

interface MutationContext {
  previousData: ShareDetailResponse | undefined;
}

async function postShareToggle(
  shareId: string,
  action: ShareToggleAction,
  state: boolean,
): Promise<ShareToggleResponse> {
  const response = await fetch(
    `/api/v1/vault/shares/${shareId}/${action}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ state }),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ShareToggleError = {
      code: "SHARE_TOGGLE_FAILED",
      message: `Share toggle failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as ShareToggleResponse;
}

export function shareToggleQueryKey(args: UseShareToggleArgs) {
  return ["share-toggle", args.shareId, args.action] as const;
}

export function useShareToggle({ shareId, action }: UseShareToggleArgs) {
  const queryClient = useQueryClient();
  const flagKey: "is_highlighted_by_owner" | "is_favourited_by_owner" =
    action === "highlight"
      ? "is_highlighted_by_owner"
      : "is_favourited_by_owner";
  return useMutation<
    ShareToggleResponse,
    ShareToggleError,
    ShareToggleVariables,
    MutationContext
  >({
    mutationFn: ({ state }) => postShareToggle(shareId, action, state),
    onMutate: async ({ state }) => {
      // Cancel any in-flight share-detail refetch so it doesn't
      // overwrite the optimistic value after we write it below.
      await queryClient.cancelQueries({
        queryKey: ["share-detail", shareId],
      });
      const previousData = queryClient.getQueryData<ShareDetailResponse>([
        "share-detail",
        shareId,
      ]);
      if (previousData) {
        // Deep-clone the share + flip the flag the Switch represents.
        // Spreading the share preserves every other field (snapshot_*,
        // tier, signed_url, etc.) so the rest of the UI keeps rendering.
        const next: ShareDetailResponse = {
          data: {
            ...previousData.data,
            share: {
              ...previousData.data.share,
              [flagKey]: state,
            },
          },
        };
        queryClient.setQueryData(["share-detail", shareId], next);
      }
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["share-detail", shareId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Reconcile both the share-detail page + the vault grid badges
      // against server truth. Partial-key match on vault-items
      // covers every filter/sort variant the user may have cached.
      queryClient.invalidateQueries({ queryKey: ["share-detail", shareId] });
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    },
  });
}
